// Session state for the Mini App: the short-lived Bearer token and the resolved
// chatId. Held in-memory (source of truth for requests) and mirrored to
// sessionStorage so a reload within the same Telegram webview survives without
// a re-verify round-trip. `client.ts` reads the token + chatId from here.
import { API_BASE } from "../api/base";
import { ApiError } from "../api/errors";
import type { AuthVerifyResult, CoreErrorBody } from "../api/types";
import { getChatId, getInitData } from "../telegram/sdk";

const TOKEN_KEY = "rocky.token";
const CHAT_KEY = "rocky.chatId";

interface SessionState {
  token?: string;
  chatId?: number;
  user?: AuthVerifyResult["user"];
}

const state: SessionState = {
  token: sessionStorage.getItem(TOKEN_KEY) ?? undefined,
  chatId: readStoredChatId(),
};

function readStoredChatId(): number | undefined {
  const raw = sessionStorage.getItem(CHAT_KEY);
  if (!raw) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

export function getToken(): string | undefined {
  return state.token;
}

export function getSessionChatId(): number | undefined {
  return state.chatId;
}

/**
 * Set the active chatId at runtime — used when the user picks a chat from the DM
 * "choose a chat" screen (which launches with no chat context). Mirrors the
 * verify path: updates the in-memory source of truth and the sessionStorage
 * mirror so a reload keeps the selection. `client.ts` (X-Chat-Id) and the
 * React Query keys both read from here, so the whole app scopes to it afterward.
 */
export function setSessionChatId(chatId: number): void {
  state.chatId = chatId;
  sessionStorage.setItem(CHAT_KEY, String(chatId));
}

export function getUser(): AuthVerifyResult["user"] | undefined {
  return state.user;
}

export function clearSession(): void {
  state.token = undefined;
  sessionStorage.removeItem(TOKEN_KEY);
}

/**
 * Verify the Telegram initData with the BFF and establish a session.
 * Resolves the chatId preferring the verified response, then the SDK launch
 * params. Throws {@link ApiError} on failure (e.g. `unauthorized`).
 *
 * Returns `{ chatId }` so callers can drive the chat-context gate: a missing
 * chatId means the app was not launched from a group.
 */
export async function verifySession(): Promise<{ chatId?: number }> {
  const initData = getInitData();
  if (!initData) {
    // No initData at all → behaves like missing chat context for the gate.
    throw new ApiError("missing_chat_context", "No Telegram initData available", 412);
  }

  const res = await fetch(`${API_BASE}/auth/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ initData }),
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as CoreErrorBody | null;
    const code = body?.error?.code ?? "internal_error";
    throw new ApiError(code, body?.error?.message ?? res.statusText, res.status);
  }

  const data = (await res.json()) as AuthVerifyResult;
  state.token = data.token;
  state.user = data.user;
  state.chatId = data.chatId ?? getChatId();

  sessionStorage.setItem(TOKEN_KEY, data.token);
  if (state.chatId !== undefined) {
    sessionStorage.setItem(CHAT_KEY, String(state.chatId));
  }

  return { chatId: state.chatId };
}
